import React from 'react';
import { useGapResolution } from '../../hooks/useGapResolution';

/**
 * The block that keeps the promise.
 *
 * <h3>Why it exists</h3>
 *
 * The surface tells a visitor it will stop and ask if it reaches something it
 * cannot work out. It stopped, and it never asked — the question went to the
 * lane as a line of text with no control attached, and the build sat there. A
 * surface that announces it will ask and then does not is worse than one that
 * never promised, because the visitor waits.
 *
 * <h3>A block, not a screen</h3>
 *
 * The approved build trail renders this decision as one node in a vertical
 * sequence. So it owns no layout beyond itself, renders nothing when nothing is
 * waiting, and can be dropped into a column today and hosted by the trail later
 * without changing.
 *
 * <h3>What it does not own</h3>
 *
 * The decision. Which steps wait, what a resolution is, when they are all
 * answered and the payload that resumes the run all belong to
 * {@link useGapResolution}, shared with the full platform's wizard so both
 * surfaces reach the same answer by the same path.
 *
 * It offers no registry search, unlike that wizard's row. Picking every step by
 * hand is the full platform's job; this surface exists for the person who wants
 * the closest candidates and a way through.
 *
 * <h3>Provenance is declared</h3>
 *
 * The hook does not default {@code source}, because a default records a human
 * decision nobody made. Every action raised here says whose it was.
 */
export default function GapDecision({ gaps = {}, runId }) {
  const { resolutions, allResolved, awaitingPerson, choose, submit, submitting, submitted, error } =
    useGapResolution({ gaps, runId });

  const stepIds = Object.keys(gaps);
  if (!stepIds.length) return null;

  /* Rows are drawn for the steps that were genuinely unmatched, chosen by their
     reviewKind rather than by whether they are answered yet — a row that vanished
     the moment it was answered would take away the only way to change your mind.
     A confirm_bind is not drawn at all: it arrived already matched, and asking
     someone to re-decide it teaches them to click past the thing that matters. */
  const hardGaps = stepIds.filter((id) => gaps[id]?.reviewKind !== 'confirm_bind');
  const confirmed = stepIds.length - hardGaps.length;

  /* No heading of its own — the trail asks the question as a node and this is
     the answer inside it. The count of what was matched still belongs here,
     because it is the reason this question is narrow. */
  return (
    <div style={{ minWidth: 0 }}>
      <p style={{ margin: '0 0 var(--p7-space-3)', font: 'var(--p7-text-caption)',
                  fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink-subtle)' }}>
        {confirmed === 1 ? 'One step' : `${confirmed} steps`} matched to things you already have.
      </p>

      {hardGaps.map((stepId) => {
        const gap = gaps[stepId];
        const chosen = resolutions[stepId];
        return (
          <div key={stepId} className="p7-card"
               style={{ padding: 'var(--p7-space-4)', marginBottom: 'var(--p7-space-3)', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <span className="p7-mono" style={{ color: 'var(--p7-ink-faint)' }}>{stepId}</span>
              <span style={{ font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                             fontWeight: 600, color: 'var(--p7-ink)' }}>
                {gap.stepName || stepId}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px',
                          marginTop: 'var(--p7-space-3)' }}>
              {(gap.candidates || []).map((c) => {
                const on = chosen?.action === 'use_match' && chosen?.registryId === c.registryId;
                return (
                  <button
                    key={c.registryId}
                    type="button"
                    aria-pressed={on}
                    className="p7-btn"
                    style={{
                      justifyContent: 'flex-start', textAlign: 'left', width: '100%',
                      borderColor: on ? 'var(--p7-accent)' : undefined,
                      background: on ? 'var(--p7-accent-bg)' : undefined,
                    }}
                    onClick={() => choose(stepId, {
                      action: 'use_match',
                      registryId: c.registryId,
                      displayName: c.displayName,
                      kind: c.kind,
                      source: 'user_resolution',
                    })}
                  >
                    <span className="p7-mono">{c.registryId}</span>
                    {c.displayName ? <span style={{ marginLeft: 8 }}>{c.displayName}</span> : null}
                    {typeof c.confidence === 'number' ? (
                      <span style={{ marginLeft: 'auto', color: 'var(--p7-ink-subtle)' }}>
                        {Math.round(c.confidence * 100)}%
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: 'var(--p7-space-3)',
                          flexWrap: 'wrap' }}>
              <button
                type="button"
                className="p7-btn p7-btn--sm"
                aria-pressed={chosen?.action === 'mint_derived'}
                onClick={() => choose(stepId, { action: 'mint_derived', source: 'user_resolution' })}
              >
                Mint a new skill for this step
              </button>
              <button
                type="button"
                className="p7-btn p7-btn--sm"
                aria-pressed={chosen?.action === 'skip_step'}
                onClick={() => choose(stepId, { action: 'skip_step', source: 'user_resolution' })}
              >
                Skip this step
              </button>
            </div>
          </div>
        );
      })}

      {error ? (
        <div className="p7-note p7-note--deny" style={{ marginBottom: 'var(--p7-space-3)' }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                    flexWrap: 'wrap' }}>
        <button
          type="button"
          className="p7-btn p7-btn--primary"
          disabled={!allResolved || submitting}
          onClick={submit}
        >
          {submitted ? 'Resuming…' : 'Resume the build'}
        </button>
        <span style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                       color: 'var(--p7-ink-subtle)' }}>
          {awaitingPerson.length === 0
            ? 'Every step has an answer.'
            : `${awaitingPerson.length} still to decide.`}
        </span>
      </div>
    </div>
  );
}
